const API_BASE = "/api";

export interface ChatMessage {
  id: number;
  author: string;
  body: string;
  ts: number;
  /** Author's equipped character / hat, for the avatar (absent on older messages). */
  character?: string | null;
  hat?: string | null;
}

interface ChatPayload {
  channel: string;
  messages: ChatMessage[];
}

/**
 * Thin API client + per-channel message cache for global / clan / DM chat. The
 * server owns ordering, retention, rate-limiting and authorization; this just
 * caches the latest snapshot per channel so a ChatScene can render frame-driven.
 * Each channel has its own monotonic ticket guard so a slow poll can't overwrite
 * the fresher list a send just returned.
 */
export class Chat {
  private cache = new Map<string, ChatMessage[]>();
  private seq = new Map<string, number>();
  private applied = new Map<string, number>();

  messages(channel: string): ChatMessage[] {
    return this.cache.get(channel)?.slice() ?? [];
  }

  clear(): void {
    this.cache.clear();
    this.seq.clear();
    this.applied.clear();
  }

  private ticket(channel: string): number {
    const n = (this.seq.get(channel) ?? 0) + 1;
    this.seq.set(channel, n);
    return n;
  }

  private apply(channel: string, ticket: number, data: Partial<ChatPayload>): void {
    if (ticket < (this.applied.get(channel) ?? 0) || !Array.isArray(data.messages)) return;
    this.applied.set(channel, ticket);
    this.cache.set(channel, data.messages);
  }

  async fetch(name: string, token: string, channel: string): Promise<void> {
    if (!name || !token || !channel) return;
    const ticket = this.ticket(channel);
    try {
      const res = await fetch(
        `${API_BASE}/chat?name=${encodeURIComponent(name)}&channel=${encodeURIComponent(channel)}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      this.apply(channel, ticket, (await res.json()) as Partial<ChatPayload>);
    } catch {
      // keep cache on network error
    }
  }

  /** Send a message. Resolves true on success; on failure returns an error code. */
  async send(
    name: string,
    token: string,
    channel: string,
    body: string,
  ): Promise<{ ok: boolean; error?: string }> {
    const ticket = this.ticket(channel);
    try {
      const res = await fetch(`${API_BASE}/chat/send`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, channel, body }),
      });
      const data = (await res.json().catch(() => ({}))) as Partial<ChatPayload> & {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !data.ok) return { ok: false, error: data.error ?? "error" };
      this.apply(channel, ticket, data);
      return { ok: true };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  async markRead(name: string, token: string, channel: string): Promise<void> {
    if (!name || !token || !channel) return;
    try {
      await fetch(`${API_BASE}/chat/read`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, channel }),
      });
    } catch {
      // best-effort
    }
  }
}
